import React, { useMemo } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Typography,
} from '@mui/material';
import {
  AssignmentTurnedIn,
  Autorenew,
  CalendarToday,
  CheckCircle,
  EditNote,
  EventAvailable,
  EventBusy,
  MailOutline,
  School,
  UploadFile,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../hooks/useNotifications';
import { NotificationEvent } from '../services/notificationsService';
import { useApp } from '../context/AppContext';
import {
  getNotificationBodyCopy,
  getNotificationIconKey,
  getNotificationLabel,
  getNotificationRoute,
  NotificationIconKey,
} from '../utils/notificationPresentation';

const getRelativeTime = (isoTimestamp: string): string => {
  const diffSeconds = Math.floor((Date.now() - new Date(isoTimestamp).getTime()) / 1000);
  if (diffSeconds < 60) return 'just now';
  if (diffSeconds < 3600) return `${Math.floor(diffSeconds / 60)}m ago`;
  if (diffSeconds < 86400) return `${Math.floor(diffSeconds / 3600)}h ago`;
  return `${Math.floor(diffSeconds / 86400)}d ago`;
};

const renderNotificationIcon = (iconKey: NotificationIconKey) => {
  switch (iconKey) {
    case 'message':
      return <MailOutline color="primary" />;
    case 'booking':
      return <CalendarToday color="primary" />;
    case 'bookingAccepted':
      return <EventAvailable color="success" />;
    case 'bookingUnavailable':
      return <EventBusy color="warning" />;
    case 'learning':
      return <School color="primary" />;
    case 'credentialSubmitted':
      return <UploadFile color="primary" />;
    case 'credentialAccepted':
      return <CheckCircle color="success" />;
    case 'credentialReview':
      return <EditNote color="info" />;
    case 'credentialRenewal':
      return <Autorenew color="warning" />;
    case 'credentialExpired':
      return <AssignmentTurnedIn color="warning" />;
    case 'update':
      return <MailOutline color="action" />;
    default: {
      const _exhaustive: never = iconKey;
      return _exhaustive;
    }
  }
};

const NotificationsPage: React.FC = () => {
  const navigate = useNavigate();
  const { state } = useApp();
  const { events, loading, error, refresh } = useNotifications();

  const displayEvents = useMemo(
    () =>
      events.map((event) => ({
        ...event,
        label: getNotificationLabel(event.type),
        bodyCopy: getNotificationBodyCopy(event),
        relativeTime: getRelativeTime(event.createdAt),
        iconKey: getNotificationIconKey(event.type),
        route: getNotificationRoute(event, state.user?.id),
      })),
    [events, state.user?.id]
  );

  const handleNotificationPress = (event: NotificationEvent, route: string | null) => {
    if (!route) {
      return;
    }

    if (route.includes('#')) {
      const [pathname, hash] = route.split('#');
      navigate(pathname);
      window.setTimeout(() => {
        document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 150);
      return;
    }

    navigate(route);
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom data-testid="web-notification-center-title">
        Notification Center
      </Typography>
      <Typography
        variant="body1"
        color="text.secondary"
        sx={{ mb: 3 }}
        data-testid="web-notification-center-subtitle"
      >
        In-app updates for messages, booking status changes, learning completions, and credential
        lifecycle reminders.
      </Typography>

      {loading && events.length === 0 ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : null}

      {error ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => void refresh()}>
              Retry
            </Button>
          }
          sx={{ mb: 2 }}
        >
          {error}
        </Alert>
      ) : null}

      {displayEvents.length === 0 && !loading ? (
        <Card>
          <CardContent>
            <Typography variant="body1">No notifications yet.</Typography>
          </CardContent>
        </Card>
      ) : null}

      {displayEvents.map((event) => (
        <Card key={event.id} sx={{ mb: 2 }} data-testid={`notification-row-${event.type}`}>
          <CardActionArea
            disabled={!event.route}
            onClick={() => handleNotificationPress(event, event.route)}
          >
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                <Box display="flex" alignItems="center" gap={1}>
                  {renderNotificationIcon(event.iconKey)}
                  <Chip
                    size="small"
                    label={event.label}
                    data-testid={`notification-type-label-${event.type}`}
                  />
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {event.relativeTime}
                </Typography>
              </Box>
              <Typography variant="subtitle1" gutterBottom>
                {event.title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {event.bodyCopy}
              </Typography>
            </CardContent>
          </CardActionArea>
        </Card>
      ))}
    </Container>
  );
};

export default NotificationsPage;
